import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, Plus, Briefcase, Palmtree, UserX, Star, Phone, MapPin, User, MessageSquare } from 'lucide-react';
import type { Technician, Assignment } from '@shared/types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getApiUrl } from '@/config';
import toast from 'react-hot-toast';

interface AssignmentModalProps {
    date: Date;
    technician?: Technician;
    availableTechnicians: Technician[];
    isOpen: boolean;
    onClose: () => void;
    onSave: (techId: string, updates: Partial<Technician>) => void;
    services?: string[];
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({
    date,
    technician,
    availableTechnicians,
    isOpen,
    onClose,
    onSave
}) => {
    // Mode: 'create' (Intervention) or 'status' (Absence/Dispo)
    const [mode, setMode] = useState<'create' | 'status'>('create');

    // Multi-select technicians
    const [selectedTechIds, setSelectedTechIds] = useState<string[]>(
        technician ? [technician.id] : []
    );

    // Form State
    const [clientName, setClientName] = useState('');
    const [startDate, setStartDate] = useState(format(date, 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(date, 'yyyy-MM-dd'));
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactName, setContactName] = useState('');
    const [task, setTask] = useState('');
    const [type, setType] = useState('Maintenance');
    const [includeWeekends, setIncludeWeekends] = useState(false);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [remarque, setRemarque] = useState('');

    // Reset state when opening for a new date/tech
    useEffect(() => {
        if (isOpen) {
            setStartDate(format(date, 'yyyy-MM-dd'));
            setEndDate(format(date, 'yyyy-MM-dd'));
            setSelectedTechIds(technician ? [technician.id] : []);
            setClientName('');
            setTask('');
            setCity('');
            setAddress('');
            setPostalCode('');
            setContactName('');
            setContactPhone('');
            setRemarque('');
            setStartTime('');
            setEndTime('');
            setIncludeWeekends(false);
        }
    }, [isOpen, date, technician]);

    if (!isOpen) return null;

    const dateKey = format(date, 'yyyy-MM-dd');
    const currentStatus = technician?.overrides?.[dateKey];

    const toggleTechnician = (techId: string) => {
        setSelectedTechIds(prev =>
            prev.includes(techId)
                ? prev.filter(id => id !== techId)
                : [...prev, techId]
        );
    };

    const handleStatusChange = (status: 'work' | 'vacation' | 'star' | undefined) => {
        if (!technician) return; // Status change usually requires a specific tech context or we iterate selected?

        // If multiple techs selected (rare for status but possible), apply to all?
        // Usually clicking a cell implies one tech. Let's assume single tech for Status Tab for safety or apply to all selected.
        // For now, let's apply to the main 'technician' prop if present, or all selectedTechIds.

        const targets = technician ? [technician.id] : selectedTechIds;

        targets.forEach(tId => {
            const tech = availableTechnicians.find(t => t.id === tId);
            if (!tech) return;

            const newOverrides = { ...(tech.overrides || {}) };
            if (status) {
                newOverrides[dateKey] = status;
            } else {
                delete newOverrides[dateKey];
            }
            onSave(tId, { overrides: newOverrides });
        });

        if (technician) onClose(); // Auto close if single tech context
    };

    const handleCreate = async () => {
        if (!clientName.trim()) return;

        // Common definition for the assignment
        const baseAssignment: Omit<Assignment, 'id'> = {
            worksiteId: `CH-${format(new Date(), 'yyMM')}-${Math.floor(1000 + Math.random() * 9000)}`,
            clientName,
            startDate,
            endDate,
            color: '#3b82f6',
            address: address || undefined,
            city: city || undefined,
            postalCode: postalCode || undefined,
            contactPhone: contactPhone || undefined,
            contactName: contactName || undefined,
            task: task || undefined,
            type: type || undefined,
            includeWeekends,
            startTime: startTime || undefined,
            endTime: endTime || undefined,
            remarque: remarque || undefined,
            status: selectedTechIds.length > 0 ? 'pending' : 'todo', // Todo if backlog, pending if assigned
        };

        if (selectedTechIds.length === 0) {
            // Case 1: BACKLOG CREATION (No Technician)
            try {
                const token = localStorage.getItem('token');
                await fetch(getApiUrl('/api/assignments'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        ...baseAssignment,
                        id: crypto.randomUUID(),
                        technicianId: null // Explicitly null for backlog
                    })
                });
                // Notify parent that saving (creation) happened, even without specific techId
                onSave('backlog', {});
                toast.success('Ajouté à la planification');
            } catch (err) {
                console.error("Failed to create backlog item", err);
                toast.error("Erreur lors de la création");
            }
        } else {
            // Case 2: ASSIGNED CREATION (One or Multiple Technicians)
            await Promise.all(selectedTechIds.map(async (techId) => {
                const tech = availableTechnicians.find(t => t.id === techId);
                if (!tech) return;

                const newAssignment: Assignment = {
                    ...baseAssignment,
                    id: crypto.randomUUID(),
                    status: 'pending'
                };

                try {
                    const token = localStorage.getItem('authToken');
                    await fetch(getApiUrl('/api/assignments'), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            ...newAssignment,
                            technicianId: techId
                        })
                    });

                    // Optimistic Local Update
                    const newAssignments = [...(tech.assignments || []), newAssignment];
                    onSave(techId, { assignments: newAssignments });

                } catch (error) {
                    console.error("Failed to save assignment:", error);
                    const newAssignments = [...(tech.assignments || []), newAssignment];
                    onSave(techId, { assignments: newAssignments });
                }
            }));
            toast.success(`${selectedTechIds.length} intervention(s) créée(s)`);
        }

        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ring-1 ring-white/5"
            >
                {/* Header */}
                <div className="p-6 pb-4 border-b border-white/5 bg-gradient-to-b from-white/5 to-transparent relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-white/30 hover:text-white hover:bg-white/10 rounded-full transition-all"
                    >
                        <X size={20} />
                    </button>

                    <h2 className="text-2xl font-bold text-white mb-1">
                        {technician ? technician.name : 'Nouveau Chantier'}
                    </h2>
                    <div className="flex items-center gap-2 text-primary/80 text-sm font-medium">
                        <Calendar size={14} />
                        <span className="capitalize">{format(date, 'EEEE d MMMM yyyy', { locale: fr })}</span>
                    </div>

                    {/* Mode Toggle */}
                    <div className="flex gap-2 mt-6 p-1 bg-black/20 rounded-xl border border-white/5">
                        <button
                            onClick={() => setMode('create')}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${mode === 'create'
                                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <Plus size={16} />
                            Nouvelle Intervention
                        </button>
                        <button
                            onClick={() => setMode('status')}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${mode === 'status'
                                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <UserX size={16} />
                            Absence / Statut
                        </button>
                    </div>
                </div>

                {/* Content Body */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">

                    {mode === 'create' ? (
                        <div className="space-y-6">
                            {/* Main Input */}
                            <div className="space-y-4">
                                <div>
                                    <input
                                        type="text"
                                        autoFocus
                                        value={clientName}
                                        onChange={e => setClientName(e.target.value)}
                                        placeholder="Nom du Chantier / Client"
                                        className="w-full bg-transparent text-2xl font-bold text-white placeholder-white/20 focus:outline-none border-b border-white/10 pb-2 focus:border-primary transition-colors"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Left Column: Timing & Tech */}
                                    <div className="space-y-4">
                                        <div className="bg-white/5 rounded-xl p-4 space-y-3 border border-white/5">
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Planning</label>
                                            <div className="grid grid-cols-2 gap-2">
                                                <input
                                                    type="date"
                                                    value={startDate}
                                                    onChange={e => setStartDate(e.target.value)}
                                                    className="bg-black/20 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:border-primary outline-none"
                                                />
                                                <input
                                                    type="date"
                                                    value={endDate}
                                                    onChange={e => setEndDate(e.target.value)}
                                                    className="bg-black/20 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:border-primary outline-none"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <input
                                                    type="time"
                                                    value={startTime}
                                                    onChange={e => setStartTime(e.target.value)}
                                                    className="bg-black/20 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:border-primary outline-none"
                                                />
                                                <input
                                                    type="time"
                                                    value={endTime}
                                                    onChange={e => setEndTime(e.target.value)}
                                                    className="bg-black/20 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:border-primary outline-none"
                                                />
                                            </div>
                                            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={includeWeekends}
                                                    onChange={e => setIncludeWeekends(e.target.checked)}
                                                    className="rounded border-white/20 bg-white/5 text-primary focus:ring-0"
                                                />
                                                Inclure week-ends
                                            </label>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Techniciens</label>
                                            <div className="flex flex-wrap gap-2">
                                                {availableTechnicians.map(tech => {
                                                    const isSelected = selectedTechIds.includes(tech.id);
                                                    return (
                                                        <button
                                                            key={tech.id}
                                                            onClick={() => toggleTechnician(tech.id)}
                                                            className={`
                                                                flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-all duration-200
                                                                ${isSelected
                                                                    ? 'bg-white/10 border-white/20 text-white'
                                                                    : 'bg-transparent border-white/5 text-gray-400 hover:border-white/20'}
                                                            `}
                                                        >
                                                            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-black" style={{ backgroundColor: tech.color }}>
                                                                {tech.name.charAt(0)}
                                                            </div>
                                                            <span className="text-xs">{tech.name}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Details */}
                                    <div className="space-y-4">
                                        <div className="bg-white/5 rounded-xl p-4 space-y-3 border border-white/5">
                                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Détails</label>
                                            <select
                                                value={type}
                                                onChange={e => setType(e.target.value)}
                                                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-primary outline-none appearance-none"
                                            >
                                                <option className="bg-slate-900" value="Maintenance">Maintenance</option>
                                                <option className="bg-slate-900" value="Reseau">Réseau</option>
                                                <option className="bg-slate-900" value="Électricité">Électricité</option>
                                                <option className="bg-slate-900" value="Plomberie">Plomberie</option>
                                                <option className="bg-slate-900" value="Climatisation">Climatisation</option>
                                            </select>
                                            <textarea
                                                value={task}
                                                onChange={e => setTask(e.target.value)}
                                                placeholder="Description de la tâche..."
                                                rows={2}
                                                className="w-full bg-transparent border-b border-white/10 py-1 text-sm text-white placeholder-gray-500 focus:border-white/30 outline-none resize-none"
                                            />
                                            <div className="relative">
                                                <MessageSquare className="absolute top-1.5 left-0 text-gray-600" size={12} />
                                                <textarea
                                                    value={remarque}
                                                    onChange={e => setRemarque(e.target.value)}
                                                    placeholder="Notes privées..."
                                                    rows={1}
                                                    className="w-full bg-transparent border-b border-white/10 py-1 pl-5 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none resize-none"
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Contact & Localisation</label>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="relative">
                                                        <User className="absolute top-2 left-0 text-gray-500" size={14} />
                                                        <input
                                                            type="text"
                                                            value={contactName}
                                                            onChange={e => setContactName(e.target.value)}
                                                            placeholder="Nom Contact"
                                                            className="w-full bg-transparent border-b border-white/10 py-1.5 pl-6 text-sm text-white placeholder-gray-500 focus:border-white/30 outline-none"
                                                        />
                                                    </div>
                                                    <div className="relative">
                                                        <Phone className="absolute top-2 left-0 text-gray-500" size={14} />
                                                        <input
                                                            type="text"
                                                            value={contactPhone}
                                                            onChange={e => setContactPhone(e.target.value)}
                                                            placeholder="Téléphone"
                                                            className="w-full bg-transparent border-b border-white/10 py-1.5 pl-6 text-sm text-white placeholder-gray-500 focus:border-white/30 outline-none"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="relative mt-2">
                                                    <MapPin className="absolute top-2 left-0 text-gray-500" size={14} />
                                                    <input
                                                        type="text"
                                                        value={address}
                                                        onChange={e => setAddress(e.target.value)}
                                                        placeholder="Adresse"
                                                        className="w-full bg-transparent border-b border-white/10 py-1.5 pl-6 text-sm text-white placeholder-gray-500 focus:border-white/30 outline-none"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <input
                                                        type="text"
                                                        value={postalCode}
                                                        onChange={e => setPostalCode(e.target.value)}
                                                        placeholder="CP"
                                                        className="col-span-1 bg-transparent border-b border-white/10 py-1.5 text-sm text-white placeholder-gray-500 focus:border-white/30 outline-none"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={city}
                                                        onChange={e => setCity(e.target.value)}
                                                        placeholder="Ville"
                                                        className="col-span-2 bg-transparent border-b border-white/10 py-1.5 text-sm text-white placeholder-gray-500 focus:border-white/30 outline-none"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Status Mode */
                        <div className="space-y-6">
                            <p className="text-gray-400 text-sm text-center">
                                Définissez le statut de <span className="text-white font-medium">{technician ? technician.name : 'tous les techniciens sélectionnés'}</span> pour le <span className="text-white">{format(date, 'd MMMM')}</span>.
                            </p>

                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => handleStatusChange('work')}
                                    className={`p-4 rounded-xl border flex flex-col items-center gap-3 transition-all ${currentStatus === 'work'
                                        ? 'bg-blue-500/20 border-blue-500 shadow-lg shadow-blue-500/10'
                                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                                        <Briefcase size={20} />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-white font-bold">Travaillé</div>
                                        <div className="text-xs text-gray-400">Présence normale</div>
                                    </div>
                                </button>

                                <button
                                    onClick={() => handleStatusChange('vacation')}
                                    className={`p-4 rounded-xl border flex flex-col items-center gap-3 transition-all ${currentStatus === 'vacation'
                                        ? 'bg-yellow-500/20 border-yellow-500 shadow-lg shadow-yellow-500/10'
                                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center text-black">
                                        <Palmtree size={20} />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-white font-bold">Congés / Absence</div>
                                        <div className="text-xs text-gray-400">Journée non travaillée</div>
                                    </div>
                                </button>

                                <button
                                    onClick={() => handleStatusChange('star')}
                                    className={`p-4 rounded-xl border flex flex-col items-center gap-3 transition-all ${currentStatus === 'star'
                                        ? 'bg-purple-500/20 border-purple-500 shadow-lg shadow-purple-500/10'
                                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white">
                                        <Star size={20} />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-white font-bold">Astreinte</div>
                                        <div className="text-xs text-gray-400">Marquer comme spécial</div>
                                    </div>
                                </button>

                                <button
                                    onClick={() => handleStatusChange(undefined)}
                                    className={`p-4 rounded-xl border flex flex-col items-center gap-3 transition-all ${!currentStatus
                                        ? 'bg-gray-500/20 border-gray-500 shadow-lg'
                                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white">
                                        <User size={20} />
                                    </div>
                                    <div className="text-center">
                                        <div className="text-white font-bold">Standard</div>
                                        <div className="text-xs text-gray-400">Effacer le statut</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/5 bg-black/20 backdrop-blur-sm flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors text-sm font-medium"
                    >
                        Annuler
                    </button>
                    {mode === 'create' && (
                        <button
                            onClick={handleCreate}
                            disabled={!clientName}
                            className={`px-6 py-2 rounded-lg font-semibold shadow-lg shadow-primary/20 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm ${selectedTechIds.length === 0 ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-500/20' : 'bg-primary hover:bg-primary/90 text-white'}`}
                        >
                            {selectedTechIds.length === 0 ? 'Sauvegarder (À Planifier)' : 'Créer le chantier'}
                        </button>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
